'use client';

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { asObject, asString, type FieldValue } from '../formFieldTypes';
import { useFormRuntimeKey } from '../formRuntimeContext';

/**
 * OtpVerificationField — Email/SMS OTP code verification for forms
 *
 * Sends a one-time code to the respondent's email or phone, then verifies
 * the 6-digit code entered.  The field value stored in form_data is:
 *   { verified: boolean, method: 'email'|'sms', target: string, verified_at?: string }
 *
 * options.method         — 'email' | 'sms' | 'both'  (default: 'email')
 * options.code_length    — Number of digits (default: 6)
 * options.ttl_seconds    — Code validity in seconds (default: 300)
 */

type OtpVerificationOptions = {
      method?: 'email' | 'sms' | 'both';
      code_length?: number;
      ttl_seconds?: number;
      [key: string]: unknown;
};

interface OtpVerificationFieldProps {
  field: {
    key: string;
    label: string;
    required?: boolean;
    readonly?: boolean;
    placeholder?: string;
    options?: unknown;
    help_text?: string;
    [key: string]: unknown;
  };
  value: FieldValue; // { verified, method, target, verified_at? }
  onChange: (value: FieldValue) => void;
  isDark: boolean;
}

type OtpStep = 'input' | 'sent' | 'verified' | 'error';

export default function OtpVerificationField({ field, value, onChange, isDark }: OtpVerificationFieldProps) {
  const opts = (field.options ?? {}) as OtpVerificationOptions;
  // Form identity (form_key / share token) — lets the OTP API attribute the
  // email to the owning client so it sends via their correspondence connector.
  const formKey = useFormRuntimeKey();
  const method = opts.method || 'email';
  const codeLength = opts.code_length || 6;

  const otpValue = asObject(value);
  const [step, setStep] = useState<OtpStep>(otpValue?.verified ? 'verified' : 'input');
  const [target, setTarget] = useState(typeof otpValue?.target === 'string' ? otpValue.target : '');
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown <= 0) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [countdown]);

  const sendCode = useCallback(async () => {
    if (!target.trim()) {
      setError(method === 'sms' ? 'Enter a phone number' : 'Enter an email address');
      return;
    }
    setSending(true);
    setError('');
    try {
      const res = await fetch('/api/v1/forms/otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'send', method: method === 'both' ? 'email' : method, target: target.trim(), field_key: field.key, code_length: codeLength, form_key: formKey || undefined }),
      });
      const data = await res.json();
      if (data.success) {
        setStep('sent');
        setCountdown(60); // 60s before resend
      } else {
        setError(data.error || 'Failed to send code');
      }
    } catch {
      setError('Network error, try again');
    } finally {
      setSending(false);
    }
  }, [target, method, field.key, codeLength, formKey]);

  const verifyCode = useCallback(async () => {
    if (code.length !== codeLength) {
      setError(`Enter the full ${codeLength}-digit code`);
      return;
    }
    setVerifying(true);
    setError('');
    try {
      const res = await fetch('/api/v1/forms/otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'verify', target: target.trim(), code, field_key: field.key }),
      });
      const data = await res.json();
      if (data.success && data.verified) {
        setStep('verified');
        onChange({ verified: true, method: method === 'both' ? 'email' : method, target: target.trim(), verified_at: new Date().toISOString() });
      } else {
        setError(data.error || 'Invalid or expired code');
      }
    } catch {
      setError('Network error, try again');
    } finally {
      setVerifying(false);
    }
  }, [code, codeLength, target, field.key, method, onChange]);

  // Individual digit inputs for the OTP code
  const handleDigitChange = useCallback((index: number, val: string) => {
    const digit = val.replace(/\D/g, '').slice(-1);
    setCode((prev) => {
      const arr = prev.split('');
      arr[index] = digit;
      const next = arr.join('').slice(0, codeLength);
      return next;
    });
    // Auto-focus next input
    if (digit && index < codeLength - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }, [codeLength]);

  const handleDigitKeyDown = useCallback((index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  }, [code]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, codeLength);
    setCode(pasted);
    // Focus the next empty input
    const nextIdx = Math.min(pasted.length, codeLength - 1);
    inputRefs.current[nextIdx]?.focus();
  }, [codeLength]);

  const baseBorder = isDark ? 'border-gray-600' : 'border-gray-300';
  const baseBg = isDark ? 'bg-gray-800' : 'bg-white';
  const baseText = isDark ? 'text-gray-200' : 'text-gray-800';

  // If already verified (from saved value), just show badge
  if (otpValue?.verified) {
    return (
      <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${isDark ? 'bg-green-900/20 border-green-800 text-green-400' : 'bg-green-50 border-green-200 text-green-700'}`}>
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div>
          <p className="text-sm font-semibold">Verified</p>
          <p className="text-xs opacity-70">{asString(otpValue?.target)} verified via {asString(otpValue?.method)}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Step 1: Enter email/phone */}
      {step === 'input' && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <input
              type={method === 'sms' ? 'tel' : 'email'}
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder={method === 'sms' ? 'Phone number' : 'Email address'}
              className={`flex-1 px-3 py-2 text-sm rounded-lg border ${baseBorder} ${baseBg} ${baseText} focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-colors`}
              disabled={field.readonly}
            />
            <button
              type="button"
              onClick={sendCode}
              disabled={sending || field.readonly || !target.trim()}
              className="px-4 py-2 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
            >
              {sending ? 'Sending…' : 'Send Code'}
            </button>
          </div>
          {field.help_text && (
            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{field.help_text}</p>
          )}
        </div>
      )}

      {/* Step 2: Enter code */}
      {step === 'sent' && (
        <div className="space-y-3">
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Code sent to <span className="font-semibold">{target}</span>. Enter the {codeLength}-digit code below.
          </p>

          {/* OTP digit boxes */}
          <div className="flex items-center justify-center gap-2" onPaste={handlePaste}>
            {Array.from({ length: codeLength }).map((_, i) => (
              <input
                key={i}
                ref={(el) => { inputRefs.current[i] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={code[i] || ''}
                onChange={(e) => handleDigitChange(i, e.target.value)}
                onKeyDown={(e) => handleDigitKeyDown(i, e)}
                className={`w-10 h-12 text-center text-lg font-bold rounded-lg border-2 ${baseBorder} ${baseBg} ${baseText} focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all`}
              />
            ))}
          </div>

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={sendCode}
              disabled={countdown > 0 || sending}
              className={`text-xs font-medium ${countdown > 0 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-700 cursor-pointer'}`}
            >
              {countdown > 0 ? `Resend in ${countdown}s` : 'Resend code'}
            </button>

            <button
              type="button"
              onClick={verifyCode}
              disabled={verifying || code.length !== codeLength}
              className="px-4 py-2 text-xs font-semibold rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {verifying ? 'Verifying…' : 'Verify'}
            </button>
          </div>

          <button
            type="button"
            onClick={() => { setStep('input'); setCode(''); setError(''); }}
            className={`text-xs ${isDark ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-500'}`}
          >
            ← Change {method === 'sms' ? 'number' : 'email'}
          </button>
        </div>
      )}

      {/* Verified state (inline) */}
      {step === 'verified' && (
        <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${isDark ? 'bg-green-900/20 border-green-800 text-green-400' : 'bg-green-50 border-green-200 text-green-700'}`}>
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="text-sm font-semibold">Verified</p>
            <p className="text-xs opacity-70">{target} verified</p>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <p className="text-xs text-red-500 font-medium">{error}</p>
      )}
    </div>
  );
}
